/**
 * Which of the desktop client's TWO scroll-wheel behaviours this browser gets.
 *
 * Upstream's whole decision is one line of
 * `CampfireScenes/Assets/Campfire/Runtime/Infrastructure/RTEditor/DesktopSceneInput.cs`:
 *
 *     canZoom = (shift || !MagicMouseDetector.IsPresent) && ...
 *     canPan  = ... && !canZoom
 *
 * ...and `MagicMouseDetector.IsPresent` is not a detector at all. Its own file
 * carries two commented-out attempts and the note "Can't reliably detect magic
 * mouse", so what ships is a compile-time constant: `true` under
 * `#if UNITY_STANDALONE_OSX`, and the `bool` default `false` on every other
 * platform. So the desktop client has two behaviours and both are "Unity":
 *
 *   - **Mac** — a bare wheel PANS on both axes, Shift + wheel zooms.
 *   - **PC** — a bare wheel ZOOMS. `canPan` can never be true for a scroll,
 *     so pan there is the middle-button drag alone.
 *
 * ⚠ This supersedes the web's previous position, which was to take the Mac half
 * unconditionally and treat the PC half as unreachable (2026-08-31, itself a
 * reversal of `AI-1276`). Both reversals were about which SINGLE behaviour to
 * ship; the platform split is what upstream actually does, and is what the web
 * now does too (user direction 2026-09-01 — "in Unity camera controls are
 * different on Mac and PC ... bring it to full parity").
 */
export type CameraScrollScheme = "mac" | "pc";

/**
 * Dev override: `"mac"` or `"pc"`. Read once per controller, so an A/B is
 * "set + reload" — the same contract as `Campfire.Dev.TextureBudget`.
 *
 * It exists because the author of a change here can only ever test the half
 * their own machine resolves to, and the other half is otherwise reachable
 * only by finding a second computer.
 */
const K_SCROLL_SCHEME_OVERRIDE_KEY = "Campfire.Dev.CameraScrollScheme";

/**
 * Pure classification, exported for the harness.
 *
 * `navigator.platform` is deprecated but universally implemented and is what
 * `EntityContextMenu.vue` already reads for the same question; the user agent
 * is the belt for a browser that has frozen or dropped it.
 *
 * ⚠ An iPad is deliberately classified `mac`, unlike
 * `classifyTextureBudgetDevice`, which goes out of its way to unmask one. That
 * function is asking about GPU memory, where an iPad is a phone; this one is
 * asking what a two-finger trackpad swipe should do, where an iPad with a Magic
 * Keyboard is a Mac. Same UA, two different right answers — do not "fix" this
 * into the other one.
 */
export function classifyCameraScrollScheme(
  userAgent: string,
  platform: string,
): CameraScrollScheme {
  if (/^Mac|iPhone|iPad|iPod/i.test(platform)) return "mac";
  if (/Macintosh|Mac OS X|iPhone|iPad|iPod/i.test(userAgent)) return "mac";
  return "pc";
}

/** Pure resolution of the effective scheme, exported for the harness. */
export function resolveCameraScrollScheme(
  scheme: CameraScrollScheme,
  overrideRaw: string | null,
): CameraScrollScheme {
  const normalized = overrideRaw?.trim().toLowerCase();
  if (normalized === "mac" || normalized === "pc") return normalized;
  return scheme;
}

/**
 * What a wheel event means under `scheme`.
 *
 * The Mac half is upstream verbatim. The PC half carries ONE deliberate
 * deviation, and it takes nothing away: on Windows `canZoom` is
 * `(shift || true)`, so Shift + wheel there does exactly what a bare wheel
 * does — it is REDUNDANT upstream, and every Windows gesture that means
 * something (bare wheel zooms, middle-drag pans) is reproduced exactly. Giving
 * that spare modifier to pan is what keeps a Windows or Linux LAPTOP usable: a
 * precision trackpad has no middle button, so strict parity would leave it with
 * no way to pan at all. The desktop client can afford that and a browser cannot.
 */
export function cameraWheelIntent(scheme: CameraScrollScheme, shiftKey: boolean): "zoom" | "pan" {
  // Mac: Shift is the ZOOM modifier over a wheel that pans.
  if (scheme === "mac") return shiftKey ? "zoom" : "pan";
  // PC: a bare wheel already zooms, so Shift is what reaches pan.
  return shiftKey ? "pan" : "zoom";
}

/** The effective scheme for this browser. Resolve once; the override needs a reload. */
export function cameraScrollScheme(): CameraScrollScheme {
  let override: string | null = null;
  try {
    override = window.localStorage.getItem(K_SCROLL_SCHEME_OVERRIDE_KEY);
  } catch {
    // Private mode / storage disabled — the classification applies.
  }
  let scheme: CameraScrollScheme = "pc";
  try {
    scheme = classifyCameraScrollScheme(navigator.userAgent ?? "", navigator.platform ?? "");
  } catch {
    // Headless — the PC scheme is the one a plain wheel mouse expects.
  }
  return resolveCameraScrollScheme(scheme, override);
}
